import { CaregiverComplianceItemMobile } from "../../messages";
import { STRINGS } from "../../strings";
import { TrainingCenterCaregiverBundle } from "../../system/AppState/AppStateTrainingCenter";
import { translate } from "../../translate";
import { getComplianceItemStatus, getInServiceComplianceStatus } from "../../utils/complianceUtils";

const MIN_DAYS_FOR_ALERT = 45;

export function getComplianceBannerDetails(
  complianceItems: CaregiverComplianceItemMobile[],
  bundles: TrainingCenterCaregiverBundle[]
) {
  const {
    notCompliantItems,
    daysForFirstItemToExpire,
    expiredComplianceItems,
    expiringComplianceItems,
    missingComplianceItems,
  } = getComplianceItemsWithIssues(complianceItems);

  const { hasLateBundle, dueSoonBundleDays } = getInserviceBundleIssues(bundles);

  const message = getExpiringComplianceMessage({
    daysForFirstItemToExpire,
    notCompliant: notCompliantItems,
    expired: expiredComplianceItems,
    expiring: expiringComplianceItems,
    missed: missingComplianceItems,
    missing: missingComplianceItems,
    dueSoonBundleDays: dueSoonBundleDays,
    hasLateBundle: hasLateBundle,
  });

  const title = getExpiringComplianceTitle(expiredComplianceItems);

  const multipleComplianceItems = (() => {
    if (expiredComplianceItems.length > 0) {
      return expiredComplianceItems.length > 1;
    }

    if (expiringComplianceItems.length > 0) {
      return expiringComplianceItems.length > 1;
    }

    return missingComplianceItems.length > 1;
  })();

  const complianceStatus = getComplianceBannerStatus({
    isCompliant:
      notCompliantItems.length === 0 &&
      expiredComplianceItems.length === 0 &&
      missingComplianceItems.length === 0 &&
      !hasLateBundle,
    daysForFirstItemToExpire,
  });

  return { title, message, multipleComplianceItems, complianceStatus };
}

function getComplianceBannerStatus({
  isCompliant,
  daysForFirstItemToExpire,
}: {
  isCompliant: boolean;
  daysForFirstItemToExpire: number | null;
}) {
  if (!isCompliant || (daysForFirstItemToExpire !== null && daysForFirstItemToExpire < 0)) {
    return { type: "notCompliant" } as const;
  }

  return daysForFirstItemToExpire === null
    ? ({ type: "compliant" } as const)
    : ({ type: "notCompliantSoon", inDays: daysForFirstItemToExpire } as const);
}

function getExpiringComplianceTitle(items: string[]) {
  return items.length > 0
    ? translate(STRINGS.EXPIRED_COMPLIANCE)
    : translate(STRINGS.EXPIRING_COMPLIANCE);
}

function getExpiringComplianceMessage(params: {
  notCompliant: string[];
  expired: string[];
  missing: string[];
  missed: string[];
  expiring: string[];
  daysForFirstItemToExpire: number;
  hasLateBundle: boolean;
  dueSoonBundleDays: number | null;
}) {
  if (params.expired.length > 0) {
    return params.expired.length === 1
      ? translate(STRINGS.YOUR_COMPLIANCE_ITEM_HAS_EXPIRED).replace(
          "%compliance_item%",
          `<strong>${params.expired[0]}</strong>`
        )
      : translate(STRINGS.YOU_ARE_OUT_OF_COMPLIANCE);
  }

  if (params.missing.length > 0) {
    return params.missing.length === 1
      ? translate(STRINGS.WE_ARE_MISSING_THE_RECORD_FOR_YOUR_COMPLIANCE_ITEM).replace(
          "%compliance_item%",
          `<strong>${params.missing[0]}</strong>`
        )
      : translate(STRINGS.YOU_HAVE_MISSING_COMPLIANCE_ITEMS);
  }

  if (params.notCompliant.length > 0) {
    return translate(STRINGS.YOU_ARE_OUT_OF_COMPLIANCE);
  }

  if (params.hasLateBundle) {
    return translate(STRINGS.YOUR_IN_SERVICE_IS_OVER_DUE);
  }

  if (params.expiring.length > 0) {
    return params.expiring.length === 1
      ? translate(STRINGS.YOUR_COMPLIANCE_ITEM_IS_DUE_IN_DAYS)
          .replace("%compliance_item%", `<strong>${params.expiring[0]}</strong>`)
          .replace("%days%", `<strong>${params.daysForFirstItemToExpire.toString()}</strong>`)
          .replace(translate(STRINGS.DAYS), `<strong>${translate(STRINGS.DAYS)}</strong>`)
      : translate(STRINGS.YOU_WILL_FALL_OUT_OF_COMPLIANCE)
          .replace("%days%", `<strong>${params.daysForFirstItemToExpire.toString()}</strong>`)
          .replace(translate(STRINGS.DAYS), `<strong>${translate(STRINGS.DAYS)}</strong>`);
  }

  if (params.dueSoonBundleDays !== null) {
    return translate(STRINGS.YOUR_IN_SERVICE_IS_DUE_IN_DAYS).replace(
      "%days%",
      params.dueSoonBundleDays.toString()
    );
  }

  return null;
}

function getComplianceItemsWithIssues(complianceItems: CaregiverComplianceItemMobile[]) {
  const expiredComplianceItems: string[] = [];
  const expiringComplianceItems: string[] = [];
  const missingComplianceItems: string[] = [];
  const notCompliantItems: string[] = [];

  let daysForFirstItemToExpire = MIN_DAYS_FOR_ALERT + 1;

  for (const item of complianceItems) {
    const status = getComplianceItemStatus(item);

    switch (status.type) {
      case "compliant":
      case "pending":
        break;

      case "dueSoon":
      case "expiresSoon":
        expiringComplianceItems.push(item.documentType.name);
        daysForFirstItemToExpire = Math.min(status.inDays, daysForFirstItemToExpire);
        break;

      case "issue": {
        if (item.complianceStatus === "Missing") {
          missingComplianceItems.push(item.documentType.name);
        }

        if (status.expiredAt === null) {
          notCompliantItems.push(item.documentType.name);
        } else {
          expiredComplianceItems.push(item.documentType.name);
          daysForFirstItemToExpire = 0;
        }
      }
    }
  }

  return {
    daysForFirstItemToExpire,
    expiredComplianceItems,
    expiringComplianceItems,
    missingComplianceItems,
    notCompliantItems,
  };
}

function getInserviceBundleIssues(bundles: TrainingCenterCaregiverBundle[]) {
  const incompleteBundles = bundles
    .map(getInServiceComplianceStatus)
    .filter((status) => status.type !== "compliant");

  const hasLateBundle = incompleteBundles.some((bundle) => bundle.type === "lateInService");

  const dueSoonBundles = incompleteBundles
    .filter(
      (bundle): bundle is { type: "inServiceDueSoon"; inDays: number } =>
        bundle.type === "inServiceDueSoon"
    )
    .map((bundle) => bundle.inDays)
    .sort((a, b) => a - b);

  return {
    hasLateBundle: hasLateBundle,
    dueSoonBundleDays: dueSoonBundles.length > 0 ? dueSoonBundles[0] : null,
  };
}
